/**
 * SuccessPredictionPanel — renders a Match's success_prediction_json,
 * with a "Generate prediction" button if none yet.
 *
 * Shows four probabilities (deal close, 3y survival, 5y survival,
 * 5y exit), the recommended action, tailwinds + headwinds, the macro
 * signals that drove the call, and the recommended path forward.
 */
import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Sparkles, Loader2,
  AlertTriangle, ArrowRight, Globe, Building2, Brain, Target,
  ChevronDown, ChevronRight, Database,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export interface SuccessSignal {
  factor?: string;
  signal?: string;
  area?: string;
  impact?: string;
  source?: string;
  supports?: string;
}

export interface MacroSnapshot {
  as_of?: string;
  summary?: string;
  government_administration?: {
    stance?: string;
    sector_signals?: Array<{ sector?: string; policy?: string; supports?: string }>;
  };
  market_trends?: {
    fed_funds_rate_estimate?: string;
    ipo_window?: string;
    public_tech_sentiment?: string;
    vc_funding_climate?: string;
    notes?: string;
  };
  world_influences?: {
    active_risks?: string[];
    supply_chain?: string;
    ai_regulation?: string;
    notes?: string;
  };
  cultural_changes?: {
    trends?: string[];
    consumer_confidence?: string;
    notes?: string;
  };
  economic_predictions?: {
    recession_probability_12m?: string;
    gdp_outlook?: string;
    sector_funding_velocity?: Array<{ sector?: string; trend?: string; notes?: string }>;
  };
}

export interface SuccessPrediction {
  deal_close_probability: number;
  startup_success_probability_3y: number;
  startup_success_probability_5y: number;
  exit_probability_5y: number;
  confidence: 'high' | 'medium' | 'low' | string;
  headline: string;
  reasoning: string;
  tailwinds: SuccessSignal[];
  headwinds: SuccessSignal[];
  key_macro_signals: SuccessSignal[];
  recommended_path: string[];
  recommended_action: string;
  key_risks: string[];
  decisive_questions: string[];
  macro_snapshot_summary?: string | null;
  macro_snapshot?: MacroSnapshot | null;
}

interface Props {
  matchId: number;
  prediction: SuccessPrediction | null;
  generatedAt?: string | null;
  onChanged?: () => void;
}

const ACTION_STYLE: Record<string, string> = {
  proceed_with_confidence: 'bg-green-100 text-green-800 border-green-200',
  proceed_with_caution: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  consider_delay: 'bg-orange-100 text-orange-800 border-orange-200',
  pass: 'bg-red-100 text-red-800 border-red-200',
};

const probColor = (p: number): string => {
  if (p >= 70) return 'text-green-700';
  if (p >= 40) return 'text-amber-700';
  return 'text-red-700';
};

const probBgColor = (p: number): string => {
  if (p >= 70) return 'bg-green-500';
  if (p >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};

const AREA_ICON: Record<string, React.ReactNode> = {
  government: <Building2 className="w-3.5 h-3.5 text-amber-600" />,
  interest_rates: <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />,
  sector_funding: <Target className="w-3.5 h-3.5 text-indigo-600" />,
  cultural: <Sparkles className="w-3.5 h-3.5 text-pink-600" />,
  geopolitical: <Globe className="w-3.5 h-3.5 text-sky-600" />,
};

const ProbBar: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div>
    <div className="flex items-center justify-between text-xs">
      <span className="font-medium text-gray-700">{label}</span>
      <span className={`font-semibold ${probColor(value)}`}>{value}%</span>
    </div>
    <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1">
      <div
        className={`h-1.5 rounded-full ${probBgColor(value)}`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  </div>
);


const SuccessPredictionPanel: React.FC<Props> = ({
  matchId, prediction, generatedAt, onChanged,
}) => {
  const [generating, setGenerating] = useState(false);
  const [showMacro, setShowMacro] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post(`/matches/${matchId}/predict-success`);
      toast.success('Success prediction queued — refresh in ~30s to see it');
      // Caller refetches; we just nudge.
      if (onChanged) {
        setTimeout(onChanged, 8000);
        setTimeout(onChanged, 25000);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to queue prediction');
    } finally {
      setGenerating(false);
    }
  };

  if (!prediction) {
    return (
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Brain className="w-4 h-4 text-indigo-500" />
              Success forecast
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              No prediction yet. Combines this match's quantitative signals
              with a current macro snapshot (US administration posture, Fed
              rate environment, sector funding flows, geopolitical risks,
              cultural shifts) to forecast deal-close probability and
              3-year / 5-year success.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Costs ~$0.03 per generation (one Claude call).
            </p>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Generate prediction
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Brain className="w-4 h-4 text-indigo-500" />
            Success forecast
          </h2>
          <p className="text-sm text-gray-700 mt-1 leading-snug font-medium">
            {prediction.headline}
          </p>
          {generatedAt && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              Generated {new Date(generatedAt).toLocaleString()} ·
              confidence: {prediction.confidence}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-1 text-xs font-semibold border rounded-md ${ACTION_STYLE[prediction.recommended_action] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            {prediction.recommended_action.replace(/_/g, ' ')}
          </span>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="text-[10px] text-gray-500 hover:text-gray-800 underline"
          >
            {generating ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </div>

      {/* Probability tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ProbBar label="Deal closes" value={prediction.deal_close_probability} />
        <ProbBar label="3y survival" value={prediction.startup_success_probability_3y} />
        <ProbBar label="5y survival" value={prediction.startup_success_probability_5y} />
        <ProbBar label="5y exit" value={prediction.exit_probability_5y} />
      </div>

      {/* Reasoning */}
      {prediction.reasoning && (
        <div className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded p-3 whitespace-pre-line">
          {prediction.reasoning}
        </div>
      )}

      {/* Tailwinds + headwinds */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Tailwinds
          </h3>
          {prediction.tailwinds.length === 0 ? (
            <p className="text-xs text-gray-400">none flagged</p>
          ) : (
            <ul className="space-y-1.5">
              {prediction.tailwinds.map((t, i) => (
                <li key={i} className="text-xs text-gray-700 leading-snug">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
                  {t.factor || t.signal}
                  {t.source && (
                    <span className="ml-1 text-[10px] text-gray-500">({t.source})</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <TrendingDown className="w-3.5 h-3.5" /> Headwinds
          </h3>
          {prediction.headwinds.length === 0 ? (
            <p className="text-xs text-gray-400">none flagged</p>
          ) : (
            <ul className="space-y-1.5">
              {prediction.headwinds.map((h, i) => (
                <li key={i} className="text-xs text-gray-700 leading-snug">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5" />
                  {h.factor || h.signal}
                  {h.source && (
                    <span className="ml-1 text-[10px] text-gray-500">({h.source})</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Key macro signals */}
      {prediction.key_macro_signals && prediction.key_macro_signals.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            Macro signals informing this call
          </h3>
          <div className="space-y-1.5">
            {prediction.key_macro_signals.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <div className="flex-shrink-0 mt-0.5">
                  {AREA_ICON[s.area || ''] || <Globe className="w-3.5 h-3.5 text-gray-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-gray-700">{s.signal}</span>
                  {s.supports && s.supports !== 'neutral' && (
                    <span className={`ml-1.5 text-[10px] uppercase font-medium ${
                      s.supports === 'tailwind' ? 'text-emerald-600' : 'text-red-600'
                    }`}>{s.supports}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended path */}
      {prediction.recommended_path && prediction.recommended_path.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <ArrowRight className="w-3.5 h-3.5" /> Recommended path
          </h3>
          <ol className="space-y-1 text-sm text-gray-700">
            {prediction.recommended_path.map((step, i) => (
              <li key={i} className="leading-snug">
                <span className="text-gray-400 mr-1">{i + 1}.</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Decisive questions */}
      {prediction.decisive_questions && prediction.decisive_questions.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Answer before committing
          </h3>
          <ul className="space-y-1 text-sm text-gray-700">
            {prediction.decisive_questions.map((q, i) => (
              <li key={i} className="leading-snug">• {q}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Full macro snapshot — collapsible. The operator asked: "we
          need to see this data, what was pulled from each area." */}
      {prediction.macro_snapshot && (
        <div className="border-t border-gray-100 pt-3">
          <button
            onClick={() => setShowMacro((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-gray-900"
          >
            {showMacro
              ? <ChevronDown className="w-3.5 h-3.5" />
              : <ChevronRight className="w-3.5 h-3.5" />}
            <Database className="w-3.5 h-3.5" />
            Macro snapshot used
            {prediction.macro_snapshot.as_of && (
              <span className="ml-1 text-[10px] text-gray-400 font-normal">
                (as of {prediction.macro_snapshot.as_of})
              </span>
            )}
          </button>
          {showMacro && (
            <div className="mt-3 space-y-3 text-sm">
              {prediction.macro_snapshot.summary && (
                <div className="text-gray-700 italic bg-gray-50 p-2 rounded">
                  {prediction.macro_snapshot.summary}
                </div>
              )}
              {/* Government administration */}
              {prediction.macro_snapshot.government_administration && (
                <MacroSection
                  icon={<Building2 className="w-3.5 h-3.5 text-amber-600" />}
                  title="Government administration"
                >
                  {prediction.macro_snapshot.government_administration.stance && (
                    <p className="text-gray-700 text-xs">{prediction.macro_snapshot.government_administration.stance}</p>
                  )}
                  {prediction.macro_snapshot.government_administration.sector_signals && (
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {prediction.macro_snapshot.government_administration.sector_signals.map((s, i) => (
                        <li key={i} className="text-gray-600">
                          <span className="font-medium text-gray-800">{s.sector}:</span> {s.policy}
                          {s.supports && (
                            <span className={`ml-1 text-[10px] uppercase ${
                              s.supports === 'tailwind' ? 'text-emerald-600' : s.supports === 'headwind' ? 'text-red-600' : 'text-gray-400'
                            }`}>{s.supports}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </MacroSection>
              )}
              {/* Market trends */}
              {prediction.macro_snapshot.market_trends && (
                <MacroSection
                  icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-600" />}
                  title="Market trends"
                >
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    <KV label="Fed funds rate" value={prediction.macro_snapshot.market_trends.fed_funds_rate_estimate} />
                    <KV label="IPO window" value={prediction.macro_snapshot.market_trends.ipo_window} />
                    <KV label="Public tech" value={prediction.macro_snapshot.market_trends.public_tech_sentiment} />
                    <KV label="VC climate" value={prediction.macro_snapshot.market_trends.vc_funding_climate} />
                  </dl>
                  {prediction.macro_snapshot.market_trends.notes && (
                    <p className="text-gray-600 text-xs mt-1">{prediction.macro_snapshot.market_trends.notes}</p>
                  )}
                </MacroSection>
              )}
              {/* World influences */}
              {prediction.macro_snapshot.world_influences && (
                <MacroSection
                  icon={<Globe className="w-3.5 h-3.5 text-sky-600" />}
                  title="World influences"
                >
                  {prediction.macro_snapshot.world_influences.active_risks && (
                    <ul className="text-xs space-y-0.5">
                      {prediction.macro_snapshot.world_influences.active_risks.map((r, i) => (
                        <li key={i} className="text-gray-700">• {r}</li>
                      ))}
                    </ul>
                  )}
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs mt-1">
                    <KV label="Supply chain" value={prediction.macro_snapshot.world_influences.supply_chain} />
                    <KV label="AI regulation" value={prediction.macro_snapshot.world_influences.ai_regulation} />
                  </dl>
                  {prediction.macro_snapshot.world_influences.notes && (
                    <p className="text-gray-600 text-xs mt-1">{prediction.macro_snapshot.world_influences.notes}</p>
                  )}
                </MacroSection>
              )}
              {/* Cultural changes */}
              {prediction.macro_snapshot.cultural_changes && (
                <MacroSection
                  icon={<Sparkles className="w-3.5 h-3.5 text-pink-600" />}
                  title="Cultural changes"
                >
                  {prediction.macro_snapshot.cultural_changes.trends && (
                    <ul className="text-xs space-y-0.5">
                      {prediction.macro_snapshot.cultural_changes.trends.map((t, i) => (
                        <li key={i} className="text-gray-700">• {t}</li>
                      ))}
                    </ul>
                  )}
                  <KV label="Consumer confidence" value={prediction.macro_snapshot.cultural_changes.consumer_confidence} />
                  {prediction.macro_snapshot.cultural_changes.notes && (
                    <p className="text-gray-600 text-xs mt-1">{prediction.macro_snapshot.cultural_changes.notes}</p>
                  )}
                </MacroSection>
              )}
              {/* Economic predictions */}
              {prediction.macro_snapshot.economic_predictions && (
                <MacroSection
                  icon={<Target className="w-3.5 h-3.5 text-indigo-600" />}
                  title="Economic predictions"
                >
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    <KV label="Recession prob (12m)" value={prediction.macro_snapshot.economic_predictions.recession_probability_12m} />
                    <KV label="GDP outlook" value={prediction.macro_snapshot.economic_predictions.gdp_outlook} />
                  </dl>
                  {prediction.macro_snapshot.economic_predictions.sector_funding_velocity && (
                    <div className="mt-1.5">
                      <div className="text-[10px] font-semibold text-gray-500 uppercase">Sector funding velocity</div>
                      <ul className="text-xs space-y-0.5 mt-0.5">
                        {prediction.macro_snapshot.economic_predictions.sector_funding_velocity.map((s, i) => (
                          <li key={i} className="text-gray-700">
                            <span className="font-medium">{s.sector}:</span> {s.trend}
                            {s.notes && <span className="text-gray-500"> — {s.notes}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </MacroSection>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};


const MacroSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="border border-gray-100 rounded p-2">
    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1">
      {icon}
      {title}
    </div>
    {children}
  </div>
);


const KV: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  value ? (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-800 font-medium">{value}</dd>
    </>
  ) : null
);

export default SuccessPredictionPanel;
